import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Send, FileText, MessageSquare, Handshake, Linkedin } from "lucide-react";
import Header from "@/components/Header";
import LeadFormModal from "@/components/LeadGenerationForm/LeadFormModal";
import CollectionIntroSection from "@/components/CollectionIntroSection";
import HowToSteps from "@/components/HowToSteps";
import { usePageTracking } from "@/hooks/usePageTracking";
import { useScrollTracking } from "@/hooks/useScrollTracking";
import { trackDestinationClicked, trackFormTriggerClicked } from "@/lib/posthog";
import { Button } from "@/components/ui/button";
const Destinations = () => {
  // Tracking hooks
  usePageTracking();
  useScrollTracking();

  // Hooks pour l'état de la page
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isHeaderOpen, setIsHeaderOpen] = useState(false);

  // Vérifier si l'écran est mobile
  const [isMobile, setIsMobile] = useState<boolean | undefined>(undefined);
  useEffect(() => {
    const mql = window.matchMedia(`(max-width: 767px)`);
    const onChange = () => {
      setIsMobile(window.innerWidth < 768);
    };
    mql.addEventListener("change", onChange);
    setIsMobile(window.innerWidth < 768);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  // Scroll vers le haut au montage
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Vérifier si nous sommes actuellement ouverts (Lundi-Vendredi, 9h-20h)
  useEffect(() => {
    const checkIfCurrentlyOpen = () => {
      const now = new Date();
      const day = now.getDay(); // 0 est Dimanche, 1-5 sont Lundi-Vendredi, 6 est Samedi
      const hour = now.getHours();
      return day >= 1 && day <= 5 && hour >= 9 && hour < 20;
    };
    setIsHeaderOpen(checkIfCurrentlyOpen());
  }, []);

  // Pour les images par région (desktop: groupes de 4, mobile: image unique)
  const [currentNAGroup, setCurrentNAGroup] = useState(0);
  const [currentEuropeGroup, setCurrentEuropeGroup] = useState(0);
  const [europeSlideDirection, setEuropeSlideDirection] = useState<"left" | "right">("right");
  const [naSlideDirection, setNaSlideDirection] = useState<"left" | "right">("right");
  
  // Mobile single-image indices
  const [europeMobileIndex, setEuropeMobileIndex] = useState(0);
  const [naMobileIndex, setNaMobileIndex] = useState(0);
  const [latAmMobileIndex, setLatAmMobileIndex] = useState(0);
  const [europeMobileDir, setEuropeMobileDir] = useState<"left" | "right">("right");
  const [naMobileDir, setNaMobileDir] = useState<"left" | "right">("right");
  const [latAmMobileDir, setLatAmMobileDir] = useState<"left" | "right">("right");

  // Slide animation variants (same as KeyEventDisplay)
  const slideVariants = {
    enter: (direction: "left" | "right") => ({
      x: direction === "right" ? "100%" : "-100%",
    }),
    center: {
      x: 0,
    },
    exit: (direction: "left" | "right") => ({
      x: direction === "right" ? "-100%" : "100%",
    }),
  };

  const handleNextNorthAmerica = () => {
    setNaSlideDirection("right");
    setCurrentNAGroup((prev) => (prev + 1) % 3);
  };
  const handlePreviousNorthAmerica = () => {
    setNaSlideDirection("left");
    setCurrentNAGroup((prev) => (prev - 1 + 3) % 3);
  };
  const handleNextEurope = () => {
    setEuropeSlideDirection("right");
    setCurrentEuropeGroup((prev) => (prev + 1) % 3);
  };
  const handlePreviousEurope = () => {
    setEuropeSlideDirection("left");
    setCurrentEuropeGroup((prev) => (prev - 1 + 3) % 3);
  };

  // Données des images par région
  const northAmericaImageGroups = [
  // Premier groupe
  [{
    src: "https://images.unsplash.com/photo-1719858403455-9a2582eca805?q=80&w=800&auto=format",
    caption: "San Francisco"
  }, {
    src: "https://images.unsplash.com/photo-1656873592841-8ae63d15be24?q=80&w=800&auto=format",
    caption: "Napa"
  }, {
    src: "https://images.unsplash.com/photo-1666969565832-b55bf42a900d?q=80&w=800&auto=format",
    caption: "Austin"
  }, {
    src: "https://images.unsplash.com/photo-1448317971280-6c74e016e49c?q=80&w=800&auto=format",
    caption: "New York"
  }],
  // Deuxième groupe
  [{
    src: "https://21180823.fs1.hubspotusercontent-na1.net/hubfs/21180823/Miami.avif",
    caption: "Miami"
  }, {
    src: "https://21180823.fs1.hubspotusercontent-na1.net/hubfs/21180823/San%20diego.avif",
    caption: "San Diego"
  }, {
    src: "https://21180823.fs1.hubspotusercontent-na1.net/hubfs/21180823/Los%20angeles.avif",
    caption: "Los Angeles"
  }, {
    src: "https://21180823.fs1.hubspotusercontent-na1.net/hubfs/21180823/Chicago.avif",
    caption: "Chicago"
  }],
  // Troisième groupe
  [{
    src: "https://21180823.fs1.hubspotusercontent-na1.net/hubfs/21180823/Joshua%20tree.jpeg",
    caption: "Joshua Tree"
  }, {
    src: "https://21180823.fs1.hubspotusercontent-na1.net/hubfs/21180823/Boston.avif",
    caption: "Boston"
  }, {
    src: "https://21180823.fs1.hubspotusercontent-na1.net/hubfs/21180823/Las%20vegas.avif",
    caption: "Las Vegas"
  }, {
    src: "https://21180823.fs1.hubspotusercontent-na1.net/hubfs/21180823/New%20orleans.avif",
    caption: "Nouvelle-Orléans"
  }]];

  // Images Amérique latine
  const latinAmericaImages = [{
    src: "https://21180823.fs1.hubspotusercontent-na1.net/hubfs/21180823/Cancun.jpeg",
    caption: "Cancún"
  }, {
    src: "https://21180823.fs1.hubspotusercontent-na1.net/hubfs/21180823/Tulum.avif",
    caption: "Tulum"
  }, {
    src: "https://21180823.fs1.hubspotusercontent-na1.net/hubfs/21180823/Costa%20rica.avif",
    caption: "Costa Rica"
  }, {
    src: "https://21180823.fs1.hubspotusercontent-na1.net/hubfs/21180823/Puerto%20Rico.avif",
    caption: "Porto Rico"
  }];

  // Images Europe
  const europeImageGroups = [
  // Premier groupe
  [{
    src: "https://21180823.fs1.hubspotusercontent-na1.net/hubfs/21180823/Lisbon.jpeg",
    caption: "Lisbonne"
  }, {
    src: "https://21180823.fs1.hubspotusercontent-na1.net/hubfs/21180823/Gran%20canaria.avif",
    caption: "Grande Canarie"
  }, {
    src: "https://21180823.fs1.hubspotusercontent-na1.net/hubfs/21180823/Barcelona.avif",
    caption: "Barcelone"
  }, {
    src: "https://21180823.fs1.hubspotusercontent-na1.net/hubfs/21180823/Madrid.avif",
    caption: "Madrid"
  }],
  // Deuxième groupe
  [{
    src: "https://21180823.fs1.hubspotusercontent-na1.net/hubfs/21180823/Prague.avif",
    caption: "Prague"
  }, {
    src: "https://21180823.fs1.hubspotusercontent-na1.net/hubfs/21180823/Tuscany.avif",
    caption: "Toscane"
  }, {
    src: "https://21180823.fs1.hubspotusercontent-na1.net/hubfs/21180823/Berlin.avif",
    caption: "Berlin"
  }, {
    src: "https://21180823.fs1.hubspotusercontent-na1.net/hubfs/21180823/Porto.avif",
    caption: "Porto"
  }],
  // Troisième groupe
  [{
    src: "https://21180823.fs1.hubspotusercontent-na1.net/hubfs/21180823/Paris.avif",
    caption: "Paris"
  }, {
    src: "https://21180823.fs1.hubspotusercontent-na1.net/hubfs/21180823/Dubrovnik.avif",
    caption: "Dubrovnik"
  }, {
    src: "https://21180823.fs1.hubspotusercontent-na1.net/hubfs/21180823/Ibiza.jpeg",
    caption: "Ibiza"
  }, {
    src: "https://21180823.fs1.hubspotusercontent-na1.net/hubfs/21180823/London.jpeg",
    caption: "Londres"
  }]];

  // Flat image arrays for mobile single-image carousel
  const allEuropeImages = europeImageGroups.flat();
  const allNAImages = northAmericaImageGroups.flat();

  // Mobile navigation handlers
  const handleNextEuropeMobile = () => { setEuropeMobileDir("right"); setEuropeMobileIndex((prev) => (prev + 1) % allEuropeImages.length); };
  const handlePrevEuropeMobile = () => { setEuropeMobileDir("left"); setEuropeMobileIndex((prev) => (prev - 1 + allEuropeImages.length) % allEuropeImages.length); };
  const handleNextNAMobile = () => { setNaMobileDir("right"); setNaMobileIndex((prev) => (prev + 1) % allNAImages.length); };
  const handlePrevNAMobile = () => { setNaMobileDir("left"); setNaMobileIndex((prev) => (prev - 1 + allNAImages.length) % allNAImages.length); };
  const handleNextLatAmMobile = () => { setLatAmMobileDir("right"); setLatAmMobileIndex((prev) => (prev + 1) % latinAmericaImages.length); };
  const handlePrevLatAmMobile = () => { setLatAmMobileDir("left"); setLatAmMobileIndex((prev) => (prev - 1 + latinAmericaImages.length) % latinAmericaImages.length); };

  // Données des destinations tendance
  const trendingDestinations = [{
    name: "Ibiza",
    villas: "88",
    image: "https://21180823.fs1.hubspotusercontent-na1.net/hubfs/21180823/1%20Hotel%20South%20Beach.webp"
  }, {
    name: "Espagne",
    villas: "197",
    image: "https://21180823.fs1.hubspotusercontent-na1.net/hubfs/21180823/547603196.jpg"
  }, {
    name: "Grèce",
    villas: "289",
    image: "https://21180823.fs1.hubspotusercontent-na1.net/hubfs/21180823/One&only%20k%C3%A9a.webp"
  }, {
    name: "Saint-Tropez et environs",
    villas: "139",
    image: "https://21180823.fs1.hubspotusercontent-na1.net/hubfs/21180823/Chateau%20de%20la%20Messardiere.jpg"
  }, {
    name: "Cap Ferret",
    villas: "62",
    image: "https://21180823.fs1.hubspotusercontent-na1.net/hubfs/21180823/Cap%20ferret.webp"
  }, {
    name: "Provence",
    villas: "139",
    image: "https://21180823.fs1.hubspotusercontent-na1.net/hubfs/21180823/Provence2.jpg"
  }];

  const [formTrigger, setFormTrigger] = useState({ location: "unknown", text: "unknown" });

  // Fonction pour ouvrir le formulaire modal
  const openForm = (triggerLocation: string, triggerText: string) => {
    setFormTrigger({ location: triggerLocation, text: triggerText });
    setIsModalOpen(true);
  };
  const closeForm = () => setIsModalOpen(false);

  // Fonction pour tracker le clic sur une destination
  const handleDestinationClick = (destinationName: string, region: 'europe' | 'northAmerica' | 'latinAmerica') => {
    trackDestinationClicked(destinationName, region, 'form_opened');
    openForm(`destinations-${region}`, destinationName);
  };

  // Fonction pour tracker les CTA
  const handleCTAClick = (location: string) => {
    trackFormTriggerClicked('Commencer', location, 'button');
    openForm(`destinations-${location}`, 'Commencer');
  };
  return <>
      {/* Header avec forceScrolled=true */}
      <Header onOpenForm={() => openForm("destinations-header", "Envoyer votre demande")} forceScrolled={true} />

      <main className="pt-16 md:pt-20 pb-10 md:pb-20">
        {/* Section d'introduction de la collection */}
        <CollectionIntroSection />
        
        {/* Titre collection */}
        <div className="relative text-center px-4 pt-6 md:pt-10 pb-8 md:pb-12">
          <div className="absolute inset-0 bg-gradient-to-b from-muted/50 to-transparent pointer-events-none" />
          <div className="relative z-10">
            <span className="inline-block text-xs font-semibold tracking-[0.25em] uppercase text-muted-foreground mb-4">
              Notre sélection
            </span>
            <h1 className="text-4xl md:text-6xl font-normal text-foreground tracking-tight mb-4">
              Découvrez la collection
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed mb-5">
              Une sélection de lieux exceptionnels pour vos événements
            </p>
            <div className="w-16 h-0.5 bg-cta-green mx-auto" />
          </div>
        </div>

        {/* Section Europe - maintenant en première position */}
        <section className="container mx-auto px-6 md:px-8 py-8 md:py-16">
          <h1 className="text-4xl md:text-5xl font-normal text-gray-900 mb-4 text-center">
            Europe
          </h1>
          <h2 className="text-base md:text-xl text-gray-600 max-w-4xl mx-auto mb-8 md:mb-12 text-center leading-relaxed">
            Explorez les destinations diverses et magnifiques d'Europe pour votre prochaine retraite d'entreprise. Des châteaux historiques 
            aux lieux urbains modernes, trouvez le cadre idéal pour les réunions, le réseautage et le renforcement d'équipe avec des 
            installations et un service de classe mondiale.
          </h2>
          
          {/* Images d'activités en Europe */}
          <section className="my-6 md:my-16">
            <div className="relative">
              {/* Mobile: single image carousel */}
              {isMobile ? (
                <>
                  <button onClick={handlePrevEuropeMobile} className="absolute -left-3 top-1/2 -translate-y-1/2 z-10 bg-white hover:bg-gray-50 rounded-full p-2 shadow-md border border-gray-200 transition-all" aria-label="Précédent">
                    <ArrowLeft className="h-4 w-4" />
                  </button>
                  <button onClick={handleNextEuropeMobile} className="absolute -right-3 top-1/2 -translate-y-1/2 z-10 bg-white hover:bg-gray-50 rounded-full p-2 shadow-md border border-gray-200 transition-all" aria-label="Suivant">
                    <ArrowRight className="h-4 w-4" />
                  </button>
                  <div className="w-full overflow-hidden px-4">
                    <AnimatePresence mode="popLayout" custom={europeMobileDir} initial={false}>
                      <motion.div key={europeMobileIndex} custom={europeMobileDir} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ x: { duration: 0.3, ease: "easeInOut" } }}>
                        <div className="relative overflow-hidden cursor-pointer" onClick={() => handleDestinationClick(allEuropeImages[europeMobileIndex].caption, 'europe')}>
                          <div className="relative" style={{ aspectRatio: "3/4" }}>
                            <img src={allEuropeImages[europeMobileIndex].src} alt={allEuropeImages[europeMobileIndex].caption} loading="lazy" width="300" height="400" className="w-full h-full object-cover rounded-lg" />
                          </div>
                          <div className="mt-4 font-normal text-gray-800 text-base text-center">{allEuropeImages[europeMobileIndex].caption}</div>
                        </div>
                      </motion.div>
                    </AnimatePresence>
                  </div>
                </>
              ) : (
                <>
                  <button onClick={handlePreviousEurope} className="absolute -left-5 top-1/2 -translate-y-1/2 z-10 bg-white hover:bg-gray-50 rounded-full p-2 shadow-md border border-gray-200 transition-all" aria-label="Précédent">
                    <ArrowLeft className="h-4 w-4" />
                  </button>
                  <button onClick={handleNextEurope} className="absolute -right-5 top-1/2 -translate-y-1/2 z-10 bg-white hover:bg-gray-50 rounded-full p-2 shadow-md border border-gray-200 transition-all" aria-label="Suivant">
                    <ArrowRight className="h-4 w-4" />
                  </button>
                  <div className="w-full overflow-hidden">
                    <AnimatePresence mode="popLayout" custom={europeSlideDirection} initial={false}>
                      <motion.div key={currentEuropeGroup} custom={europeSlideDirection} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ x: { duration: 0.3, ease: "easeInOut" } }} className="grid grid-cols-4 gap-4">
                        {europeImageGroups[currentEuropeGroup].map((image, index) => <div key={index}>
                            <div className="relative overflow-hidden cursor-pointer" onClick={() => handleDestinationClick(image.caption, 'europe')}>
                              <div className="relative" style={{ aspectRatio: "3/4" }}>
                                <img src={image.src} alt={image.caption} loading="lazy" width="300" height="400" className="w-full h-full object-cover rounded-lg transition-transform duration-300 hover:scale-105" />
                              </div>
                              <div className="mt-6 font-normal text-gray-800 text-lg">{image.caption}</div>
                            </div>
                          </div>)}
                      </motion.div>
                    </AnimatePresence>
                  </div>
                </>
              )}
            </div>
          </section>
          
          {/* Bouton CTA */}
          <div className="text-center mt-10 mb-16">
            <button onClick={() => handleCTAClick('europe-section')} className="rounded-xl px-4 py-2 text-sm font-semibold bg-cta-green text-cta-green-foreground hover:bg-cta-green/90 hover:shadow-xl hover:shadow-cta-green/30 transition-all duration-300 hover:scale-105 group">
              <span>Commencer</span>
              <ArrowRight className="h-4 w-4 ml-1 inline-block transition-transform duration-200 group-hover:translate-x-1" />
            </button>
          </div>
        </section>
        
        {/* Section Amérique du Nord - maintenant en deuxième position */}
        <section className="bg-slate-50 py-8 md:py-16">
          <div className="container mx-auto px-6 md:px-8">
            <h1 className="text-4xl md:text-5xl font-normal text-gray-900 mb-4 text-center">
              Amérique du Nord
            </h1>
            <h2 className="text-base md:text-xl text-gray-600 max-w-4xl mx-auto mb-8 md:mb-12 text-center leading-relaxed">
              Découvrez une large gamme de retraites d'entreprise à travers l'Amérique du Nord, où vous pourrez allier affaires et aventure. 
              Des villes animées aux espaces naturels pittoresques, explorez des lieux dotés d'installations haut de gamme, de services personnalisés 
              et d'activités immersives de renforcement d'équipe.
            </h2>
          </div>
          
          {/* Bande d'images d'activités */}
          <section className="container mx-auto px-6 md:px-8 my-6 md:my-16">
            <div className="relative">
              {isMobile ? (
                <>
                  <button onClick={handlePrevNAMobile} className="absolute -left-3 top-1/2 -translate-y-1/2 z-10 bg-white hover:bg-gray-50 rounded-full p-2 shadow-md border border-gray-200 transition-all" aria-label="Précédent">
                    <ArrowLeft className="h-4 w-4" />
                  </button>
                  <button onClick={handleNextNAMobile} className="absolute -right-3 top-1/2 -translate-y-1/2 z-10 bg-white hover:bg-gray-50 rounded-full p-2 shadow-md border border-gray-200 transition-all" aria-label="Suivant">
                    <ArrowRight className="h-4 w-4" />
                  </button>
                  <div className="w-full overflow-hidden px-4">
                    <AnimatePresence mode="popLayout" custom={naMobileDir} initial={false}>
                      <motion.div key={naMobileIndex} custom={naMobileDir} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ x: { duration: 0.3, ease: "easeInOut" } }}>
                        <div className="relative overflow-hidden cursor-pointer" onClick={() => handleDestinationClick(allNAImages[naMobileIndex].caption, 'northAmerica')}>
                          <div className="relative" style={{ aspectRatio: "3/4" }}>
                            <img src={allNAImages[naMobileIndex].src} alt={allNAImages[naMobileIndex].caption} loading="lazy" width="300" height="400" className="w-full h-full object-cover rounded-lg" />
                          </div>
                          <div className="mt-4 font-normal text-gray-800 text-base text-center">{allNAImages[naMobileIndex].caption}</div>
                        </div>
                      </motion.div>
                    </AnimatePresence>
                  </div>
                </>
              ) : (
                <>
                  <button onClick={handlePreviousNorthAmerica} className="absolute -left-5 top-1/2 -translate-y-1/2 z-10 bg-white hover:bg-gray-50 rounded-full p-2 shadow-md border border-gray-200 transition-all" aria-label="Précédent">
                    <ArrowLeft className="h-4 w-4" />
                  </button>
                  <button onClick={handleNextNorthAmerica} className="absolute -right-5 top-1/2 -translate-y-1/2 z-10 bg-white hover:bg-gray-50 rounded-full p-2 shadow-md border border-gray-200 transition-all" aria-label="Suivant">
                    <ArrowRight className="h-4 w-4" />
                  </button>
                  <div className="w-full overflow-hidden">
                    <AnimatePresence mode="popLayout" custom={naSlideDirection} initial={false}>
                      <motion.div key={currentNAGroup} custom={naSlideDirection} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ x: { duration: 0.3, ease: "easeInOut" } }} className="grid grid-cols-4 gap-4">
                        {northAmericaImageGroups[currentNAGroup].map((image, index) => <div key={index}>
                            <div className="relative overflow-hidden cursor-pointer" onClick={() => handleDestinationClick(image.caption, 'northAmerica')}>
                              <div className="relative" style={{ aspectRatio: "3/4" }}>
                                <img src={image.src} alt={image.caption} loading="lazy" width="300" height="400" className="w-full h-full object-cover rounded-lg transition-transform duration-300 hover:scale-105" />
                              </div>
                              <div className="mt-6 font-normal text-gray-800 text-lg">{image.caption}</div>
                            </div>
                          </div>)}
                      </motion.div>
                    </AnimatePresence>
                  </div>
                </>
              )}
            </div>
          </section>
          
          {/* Bouton CTA */}
          <div className="container mx-auto px-6 md:px-8">
            <div className="text-center mt-10 mb-16">
              <button onClick={() => handleCTAClick('north-america-section')} className="rounded-xl px-4 py-2 text-sm font-semibold bg-cta-green text-cta-green-foreground hover:bg-cta-green/90 hover:shadow-xl hover:shadow-cta-green/30 transition-all duration-300 hover:scale-105 group">
                <span>Commencer</span>
                <ArrowRight className="h-4 w-4 ml-1 inline-block transition-transform duration-200 group-hover:translate-x-1" />
              </button>
            </div>
          </div>
        </section>
        
        {/* Section Amérique latine - maintenant en troisième position */}
        <section className="container mx-auto px-6 md:px-8 py-8 md:py-16">
          <h1 className="text-4xl md:text-5xl font-normal text-gray-900 mb-4 text-center">
            Amérique latine
          </h1>
          <h2 className="text-base md:text-xl text-gray-600 max-w-4xl mx-auto mb-8 md:mb-12 text-center leading-relaxed">
            Découvrez le potentiel de l'Amérique latine pour votre prochain événement d'entreprise. Que vous recherchiez des destinations 
            tropicales ou des retraites en montagne, nos lieux offrent des espaces de réunion exceptionnels, des expériences culturelles 
            et des hébergements luxueux.
          </h2>
          
          {/* Images d'activités d'Amérique latine */}
          <section className="my-6 md:my-16">
            <div className="relative">
              {isMobile ? (
                <>
                  <button onClick={handlePrevLatAmMobile} className="absolute -left-3 top-1/2 -translate-y-1/2 z-10 bg-white hover:bg-gray-50 rounded-full p-2 shadow-md border border-gray-200 transition-all" aria-label="Précédent">
                    <ArrowLeft className="h-4 w-4" />
                  </button>
                  <button onClick={handleNextLatAmMobile} className="absolute -right-3 top-1/2 -translate-y-1/2 z-10 bg-white hover:bg-gray-50 rounded-full p-2 shadow-md border border-gray-200 transition-all" aria-label="Suivant">
                    <ArrowRight className="h-4 w-4" />
                  </button>
                  <div className="w-full overflow-hidden px-4">
                    <AnimatePresence mode="popLayout" custom={latAmMobileDir} initial={false}>
                      <motion.div key={latAmMobileIndex} custom={latAmMobileDir} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ x: { duration: 0.3, ease: "easeInOut" } }}>
                        <div className="relative overflow-hidden cursor-pointer" onClick={() => handleDestinationClick(latinAmericaImages[latAmMobileIndex].caption, 'latinAmerica')}>
                          <div className="relative" style={{ aspectRatio: "3/4" }}>
                            <img src={latinAmericaImages[latAmMobileIndex].src} alt={latinAmericaImages[latAmMobileIndex].caption} loading="lazy" width="300" height="400" className="w-full h-full object-cover rounded-lg" />
                          </div>
                          <div className="mt-4 font-normal text-gray-800 text-base text-center">{latinAmericaImages[latAmMobileIndex].caption}</div>
                        </div>
                      </motion.div>
                    </AnimatePresence>
                  </div>
                </>
              ) : (
                <div className="w-full">
                  <div className="flex overflow-x-scroll snap-x snap-mandatory scrollbar-none pb-8">
                    {latinAmericaImages.map((image, index) => <div key={index} className="w-1/4 flex-shrink-0 snap-center px-2">
                        <div className="relative overflow-hidden cursor-pointer" onClick={() => handleDestinationClick(image.caption, 'latinAmerica')}>
                          <div className="relative" style={{ aspectRatio: "3/4" }}>
                            <img src={image.src} alt={image.caption} loading="lazy" width="300" height="400" className="w-full h-full object-cover rounded-lg transition-transform duration-300 hover:scale-105" />
                          </div>
                          <div className="mt-6 font-normal text-gray-800 text-lg">{image.caption}</div>
                        </div>
                      </div>)}
                  </div>
                </div>
              )}
            </div>
          </section>
          
          {/* Bouton CTA */}
          <div className="text-center mt-10 mb-16">
            <button onClick={() => handleCTAClick('latin-america-section')} className="rounded-xl px-4 py-2 text-sm font-semibold bg-cta-green text-cta-green-foreground hover:bg-cta-green/90 hover:shadow-xl hover:shadow-cta-green/30 transition-all duration-300 hover:scale-105 group">
              <span>Commencer</span>
              <ArrowRight className="h-4 w-4 ml-1 inline-block transition-transform duration-200 group-hover:translate-x-1" />
            </button>
          </div>
        </section>
        
        {/* Section Destinations tendance */}
        <section className="container mx-auto px-6 md:px-8 py-6 md:py-16 mb-4 md:mb-10">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} viewport={{
          once: true
        }} className="text-3xl md:text-5xl sf-font text-center mb-6 md:mb-12">
            Destinations tendance
          </motion.h2>
          
          {isMobile ?
        // Mobile: Carousel
        <>
              <div className="w-full">
                <div className="flex overflow-x-scroll snap-x snap-mandatory scrollbar-none pb-8">
                  {trendingDestinations.map((destination, index) => <div key={index} className="w-full flex-shrink-0 snap-center px-2">
                      <motion.div initial={{
                  opacity: 0,
                  y: 30
                }} whileInView={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  duration: 0.5,
                  delay: index * 0.1
                }} viewport={{
                  once: true
                }} className="group cursor-pointer" onClick={() => handleDestinationClick(destination.name, 'europe')}>
                        <div className="overflow-hidden rounded-lg mb-6">
                          <div className="relative" style={{ aspectRatio: "3/4" }}>
                            <img src={destination.image} alt={destination.name} loading="lazy" width="300" height="400" className="w-full h-full object-cover rounded-lg transition-transform duration-500 group-hover:scale-105" />
                          </div>
                        </div>
                        <h3 className="text-xl sf-font font-normal">{destination.name}</h3>
                        <p className="text-gray-600 sf-font">{destination.villas} villas à louer</p>
                      </motion.div>
                    </div>)}
                </div>
                <div className="flex justify-center gap-2 mt-4">
                  {trendingDestinations.map((_, index) => <div key={index} className="w-2 h-2 rounded-full bg-gray-300" />)}
                </div>
              </div>
            </> :
        // Desktop: Grid
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {trendingDestinations.map((destination, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} viewport={{
            once: true
          }} className="group cursor-pointer" onClick={() => handleDestinationClick(destination.name, 'europe')}>
                  <div className="overflow-hidden rounded-lg mb-3 md:mb-4">
                    <img src={destination.image} alt={destination.name} loading="lazy" width="400" height="280" className="w-full h-[280px] object-cover transition-transform duration-500 group-hover:scale-105" />
                  </div>
                  <h3 className="text-xl sf-font font-normal">{destination.name}</h3>
                  <p className="text-gray-600 sf-font">{destination.villas} villas à louer</p>
                </motion.div>)}
            </div>}
        </section>
        
        <HowToSteps onOpenForm={() => openForm("destinations-how-to", "Commencer")} />
        
        {/* Destination en vedette */}
        <section className="mb-16">
          <div className="relative w-full">
            <img src="/lovable-uploads/destinations/beach-resort-cta.webp" alt="Destination en vedette" loading="lazy" width="1920" height="600" className="w-full h-[37.5vh] md:h-[60vh] object-cover" />
            <div className="absolute inset-0 bg-black bg-opacity-60">
              <div className="flex flex-col items-center justify-center h-full px-6 text-center">
                <h1 className="text-lg md:text-5xl text-white font-normal mb-8 max-w-4xl">Créez votre événement idéal – soumettez votre demande maintenant et profitez d'offres exclusives</h1>
                <button onClick={() => openForm("destinations-featured-cta", "Commencer")} className="rounded-xl group bg-white text-black hover:bg-white/90 hover:shadow-xl transition-all duration-300 ease-out transform hover:scale-105 text-sm px-4 py-2 font-semibold">
                  Commencer
                  
                </button>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Formulaire modal */}
      <LeadFormModal isOpen={isModalOpen} onClose={closeForm} triggerLocation={formTrigger.location} triggerText={formTrigger.text} />
      
      {/* Pied de page */}
      <footer className="bg-gradient-to-b from-gray-900 to-black text-white py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-8">
            {/* Logo */}
            <div>
              <img src="/lovable-uploads/0174e7d3-9a04-4db3-b808-8ef7469fc5a8.png" alt="Naboo" className="h-8" onError={(e) => {
              console.warn("Échec de chargement du logo de pied de page");
              e.currentTarget.style.display = 'none';
            }} />
            </div>
            
            {/* Liens légaux */}
            <div className="flex flex-col md:flex-row items-center gap-4 md:gap-8 text-sm">
              <span className="font-normal">© SAS NABOO GROUP 2026</span>
              <a href="https://www.naboo.app/en-us/privacy-policy?utm_source=landing_page_fr&utm_medium=referral&utm_campaign=footer_link" target="_blank" rel="noopener noreferrer" className="hover:text-gray-300 transition-colors">
                Mentions légales & Politique de confidentialité
              </a>
              <a href="https://www.naboo.app/en-us/terms-of-use?utm_source=landing_page_fr&utm_medium=referral&utm_campaign=footer_link" target="_blank" rel="noopener noreferrer" className="hover:text-gray-300 transition-colors">
                Conditions générales d'utilisation
              </a>
              <a href="javascript:void(0)" onClick={() => {
              if ((window as any).openAxeptioCookies) {
                (window as any).openAxeptioCookies();
              }
            }} className="text-sm leading-5 antialiased whitespace-nowrap inline-block text-white hover:text-gray-300 transition-colors hover:underline underline-offset-2 cursor-pointer">
                Mes préférences de cookies
              </a>
            </div>
            
            {/* Réseaux sociaux */}
            <div>
              <a href="https://www.linkedin.com/company/naboo-app" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 transition-colors" aria-label="LinkedIn">
                <Linkedin className="h-5 w-5 text-white fill-current" />
              </a>
            </div>
          </div>
        </div>
      </footer>
    </>;
};
export default Destinations;